/**
 * How a patient is named on screen (AMP-143, AMP-156): by first and last name, with the MRN the
 * practice knows them by. The internal patient ID (P-xxxx) is a system key, not something staff
 * recognize, so it is not shown as the patient's identity.
 */
interface NamedPatient {
  firstName?: string;
  lastName?: string;
  patientId?: string;
  medicalRecord?: { mrn?: string };
}

export function patientName(patient: NamedPatient, fallback = 'Unnamed patient'): string {
  const name = [patient.firstName, patient.lastName].map(part => part?.trim()).filter(Boolean).join(' ');
  return name || fallback;
}

/** "MRN: 12345", or "No MRN" when the practice has not recorded one. */
export function patientMrnLabel(patient: NamedPatient): string {
  const mrn = patient.medicalRecord?.mrn?.trim();
  return mrn ? `MRN: ${mrn}` : 'No MRN';
}

/**
 * The identifier shown next to a patient's name. Standalone practices know their patients by MRN.
 * In connected mode the patient ID is the EHR's own key, which staff do use, so it stays.
 */
export function patientIdLabel(patient: NamedPatient, operationMode: 'standalone' | 'connected'): string {
  return operationMode === 'standalone' ? patientMrnLabel(patient) : `ID: ${patient.patientId || 'N/A'}`;
}
